import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PlusCircle, Stethoscope, FolderClosed, MapPin, ChevronRight, ArrowRight, Users, Bell, CalendarDays, Pencil, LocateFixed } from "lucide-react";
import { CareShell } from "@/components/CareShell";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/api";
import { detectCareLocation } from "@/lib/careLocation";
import { CARE_WELCOME_KEY } from "@/pages/care/CareWelcome";

export default function CareHome() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [meta, setMeta] = useState({ countries: [], languages: [] });
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ country: "", city: "", preferred_language: "" });
  const [saving, setSaving] = useState(false);
  const [editingLocation, setEditingLocation] = useState(false);
  const [locating, setLocating] = useState(false);

  const useMyLocation = async () => {
    setLocating(true);
    try {
      const res = await detectCareLocation();
      if (!res.country && !res.city) {
        toast.error("Couldn't detect your location. Please enter it manually.");
        return;
      }
      setForm((f) => ({
        ...f,
        city: res.city || f.city,
        country: res.supported ? res.country : f.country,
      }));
      if (res.country && !res.supported) {
        toast.info(`We detected ${res.country}, which isn't supported yet — please pick a country from the list.`);
      } else {
        toast.success("Location detected.");
      }
    } catch (e) {
      toast.error(e.message || "Couldn't get your location.");
    } finally {
      setLocating(false);
    }
  };

  const startEditLocation = () => {
    setForm({
      country: profile?.country || "",
      city: profile?.city || "",
      preferred_language: profile?.preferred_language || "",
    });
    setEditingLocation(true);
  };

  useEffect(() => {
    if (!localStorage.getItem(CARE_WELCOME_KEY)) {
      navigate("/care/welcome", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    (async () => {
      try {
        const [p, m, c] = await Promise.all([
          api.get("/care/profile"),
          api.get("/care/meta"),
          api.get("/care/cases"),
        ]);
        setProfile(p.data);
        setMeta(m.data);
        setCases(c.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const saveProfile = async () => {
    if (!form.country) return toast.error("Please choose your country.");
    setSaving(true);
    try {
      const { data } = await api.put("/care/profile", form);
      setProfile(data);
      setEditingLocation(false);
      toast.success("Care location saved.");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const openCount = cases.length;

  // Upcoming appointment reminders from booked consultations (local + video).
  const todayStr = new Date().toISOString().slice(0, 10);
  const appointments = [];
  cases.forEach((c) => {
    if (c.consultation?.preferred_date && c.consultation.preferred_date >= todayStr) {
      appointments.push({
        caseId: c.id, kind: "In-person consultation",
        who: c.consultation.doctor?.name, place: c.consultation.doctor?.clinic,
        date: c.consultation.preferred_date, time: c.consultation.preferred_time,
      });
    }
    if (c.video_consultation?.date && c.video_consultation.date >= todayStr) {
      appointments.push({
        caseId: c.id, kind: "Video review",
        who: c.video_consultation.specialist?.name, place: "Video call",
        date: c.video_consultation.date, time: c.video_consultation.time,
      });
    }
  });
  appointments.sort((a, b) => (a.date + (a.time || "")).localeCompare(b.date + (b.time || "")));
  const nextAppt = appointments[0];

  if (loading) {
    return (
      <CareShell active="home" title="Care">
        <div className="space-y-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-28 bg-[#1A1A1A] border border-[#333333] rounded-2xl animate-pulse" />
          ))}
        </div>
      </CareShell>
    );
  }

  return (
    <CareShell active="home" title="Care">
      <div>
        <p className="text-[#A1A1AA]">Welcome to Care</p>
        <h1 className="font-head text-3xl font-extrabold text-[#F8FAFC] mt-1">Get a second opinion</h1>
      </div>

      {nextAppt && (
        <button
          data-testid="care-reminder"
          onClick={() => navigate(`/care/cases/${nextAppt.caseId}`)}
          className="mt-5 w-full text-left bg-[#EF9F27]/10 border border-[#EF9F27]/40 rounded-2xl p-5 transition-transform active:scale-[0.99]"
        >
          <div className="flex items-center gap-2 text-[#EF9F27] text-sm font-bold">
            <Bell size={15} /> Upcoming appointment
          </div>
          <h3 className="font-head text-lg font-bold text-[#F8FAFC] mt-1">
            {nextAppt.kind} with {nextAppt.who}
          </h3>
          <p className="text-sm text-[#F8FAFC] mt-1 inline-flex items-center gap-2">
            <CalendarDays size={15} className="text-[#EF9F27]" />
            {new Date(nextAppt.date + "T00:00:00").toLocaleDateString(undefined, {
              weekday: "long", day: "numeric", month: "long",
            })}
            {nextAppt.time ? ` at ${nextAppt.time}` : ""}
          </p>
          <p className="text-xs text-[#A1A1AA] mt-1">{nextAppt.place}</p>
        </button>
      )}

      {!profile || editingLocation ? (
        <div
          data-testid="care-setup-card"
          className="mt-5 bg-[#1A1A1A] border border-[#333333] rounded-2xl p-5"
        >
          <div className="flex items-center gap-2 mb-1">
            <MapPin size={18} className="text-[#EF9F27]" />
            <h2 className="font-head text-lg font-bold text-[#F8FAFC]">
              {editingLocation ? "Update your care location" : "Set up your care location"}
            </h2>
          </div>
          <p className="text-sm text-[#A1A1AA] mb-4">
            Tell us where you're based so we can match local doctors.
          </p>
          <button
            data-testid="care-use-location-btn"
            onClick={useMyLocation}
            disabled={locating}
            className="mb-4 w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#EF9F27] text-[#EF9F27] font-bold px-4 py-3 min-h-[44px] transition-transform active:scale-[0.97] disabled:opacity-60"
          >
            <LocateFixed size={16} /> {locating ? "Detecting…" : "Use my current location"}
          </button>
          <label className="block text-sm font-medium text-[#A1A1AA] mb-1.5">Country</label>
          <select
            data-testid="care-country-select"
            value={form.country}
            onChange={(e) => setForm({ ...form, country: e.target.value })}
            className="w-full bg-[#111111] border border-[#333333] rounded-xl px-4 py-3.5 min-h-[44px] text-[#F8FAFC] focus:outline-none focus:border-[#EF9F27] mb-3"
          >
            <option value="">Select country</option>
            {(meta.all_countries || []).map((c) => {
              const served = (meta.countries || []).includes(c);
              return (
                <option key={c} value={c} disabled={!served}>
                  {served ? c : `${c} — Coming soon`}
                </option>
              );
            })}
          </select>
          <label className="block text-sm font-medium text-[#A1A1AA] mb-1.5">City (optional)</label>
          <input
            data-testid="care-city-input"
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
            placeholder="e.g. Accra"
            className="w-full bg-[#111111] border border-[#333333] rounded-xl px-4 py-3.5 min-h-[44px] text-[#F8FAFC] placeholder:text-[#666666] focus:outline-none focus:border-[#EF9F27] mb-3"
          />
          <label className="block text-sm font-medium text-[#A1A1AA] mb-1.5">Preferred language (optional)</label>
          <select
            data-testid="care-language-select"
            value={form.preferred_language}
            onChange={(e) => setForm({ ...form, preferred_language: e.target.value })}
            className="w-full bg-[#111111] border border-[#333333] rounded-xl px-4 py-3.5 min-h-[44px] text-[#F8FAFC] focus:outline-none focus:border-[#EF9F27] mb-4"
          >
            <option value="">No preference</option>
            {meta.languages.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
          <div className="flex gap-3">
            {editingLocation && (
              <button
                data-testid="care-cancel-location-btn"
                onClick={() => setEditingLocation(false)}
                disabled={saving}
                className="flex-1 rounded-full bg-[#222222] border border-[#333333] text-[#F8FAFC] font-bold px-6 py-3.5 min-h-[48px] transition-transform active:scale-[0.97] disabled:opacity-60"
              >
                Cancel
              </button>
            )}
            <button
              data-testid="care-save-profile-btn"
              onClick={saveProfile}
              disabled={saving}
              className="flex-1 rounded-full bg-[#EF9F27] text-[#111111] font-bold px-6 py-3.5 min-h-[48px] transition-transform active:scale-[0.97] disabled:opacity-60"
            >
              {saving ? "Saving…" : editingLocation ? "Save location" : "Save & continue"}
            </button>
          </div>
        </div>
      ) : (
        <button
          data-testid="care-location-chip"
          onClick={startEditLocation}
          aria-label={`Care location: ${profile.city ? profile.city + ", " : ""}${profile.country}. Tap to change.`}
          className="mt-4 inline-flex items-center gap-2 bg-[#222222] border border-[#333333] rounded-full px-4 py-2 text-sm font-semibold text-[#F8FAFC] hover:bg-[#333333] transition-colors active:scale-95"
        >
          <MapPin size={15} className="text-[#EF9F27]" />
          {profile.city ? `${profile.city}, ` : ""}{profile.country}
          <Pencil size={13} className="text-[#A1A1AA] ml-0.5" />
        </button>
      )}

      <button
        data-testid="care-start-review-card"
        onClick={() => navigate("/care/new")}
        className="mt-5 w-full text-left bg-[#EF9F27] rounded-2xl p-6 text-[#111111] transition-transform active:scale-[0.98]"
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-head text-2xl font-extrabold">Start a specialist review</h2>
            <p className="mt-1 font-medium opacity-90">Share your concern & records for a written second opinion</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-black/15 flex items-center justify-center shrink-0">
            <PlusCircle size={26} />
          </div>
        </div>
      </button>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <button
          data-testid="care-cases-tile"
          onClick={() => navigate("/care/cases")}
          className="text-left bg-[#1A1A1A] border border-[#333333] rounded-2xl p-4 hover:bg-[#222222] transition-colors"
        >
          <Stethoscope size={20} className="text-[#EF9F27]" />
          <p className="mt-3 text-2xl font-extrabold text-[#F8FAFC] font-head">{openCount}</p>
          <p className="text-sm text-[#A1A1AA]">My cases</p>
        </button>
        <button
          data-testid="care-records-tile"
          onClick={() => navigate("/care/records")}
          className="text-left bg-[#1A1A1A] border border-[#333333] rounded-2xl p-4 hover:bg-[#222222] transition-colors"
        >
          <FolderClosed size={20} className="text-[#EF9F27]" />
          <p className="mt-3 font-head text-lg font-bold text-[#F8FAFC]">Records</p>
          <p className="text-sm text-[#A1A1AA]">Manage documents</p>
        </button>
      </div>

      <button
        data-testid="care-browse-specialists-card"
        onClick={() => navigate("/care/specialists")}
        className="mt-3 w-full text-left bg-[#1A1A1A] border border-[#333333] rounded-2xl p-4 flex items-center justify-between hover:bg-[#222222] transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#EF9F27]/15 flex items-center justify-center">
            <Users size={20} className="text-[#EF9F27]" />
          </div>
          <div>
            <h3 className="font-head text-lg font-bold text-[#F8FAFC]">Browse specialists</h3>
            <p className="text-sm text-[#A1A1AA]">See credentials & focus before you choose</p>
          </div>
        </div>
        <ChevronRight className="text-[#666666] shrink-0" />
      </button>

      {cases.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-head text-xl font-bold text-[#F8FAFC]">Recent cases</h2>
            <button
              data-testid="care-view-all-cases"
              onClick={() => navigate("/care/cases")}
              className="text-sm font-semibold text-[#EF9F27] inline-flex items-center gap-1"
            >
              View all <ArrowRight size={14} />
            </button>
          </div>
          <ul className="space-y-3">
            {cases.slice(0, 3).map((c) => (
              <li key={c.id}>
                <button
                  data-testid={`care-case-item-${c.id}`}
                  onClick={() => navigate(`/care/cases/${c.id}`)}
                  className="w-full text-left bg-[#1A1A1A] border border-[#333333] rounded-2xl p-4 flex items-center justify-between hover:bg-[#222222] transition-colors"
                >
                  <div className="min-w-0">
                    <h3 className="font-head font-bold text-[#F8FAFC] truncate">{c.title}</h3>
                    <p className="text-sm text-[#A1A1AA]">{c.specialty}</p>
                  </div>
                  <ChevronRight className="text-[#666666] shrink-0" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </CareShell>
  );
}
